import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import MapLibreGL from 'maplibre-gl';
import { Map as MapIcon, AlertCircle } from 'lucide-react';
import {
  Map,
  useMap,
  MapMarker,
  MarkerContent,
  MarkerPopup,
  MapControls,
  type MapViewport,
} from '@/components/ui/map';
import { cn } from '@/lib/utils';

interface Hotel {
  id: string;
  name: string;
  location: string;
  coordinates?: {
    latitude: number;
    longitude: number;
    address?: string;
  } | null;
  price?: {
    amount: number;
    currency: string;
  };
  starRating?: number;
  amenities?: string[];
  images?: string[];
}

interface HotelFilters {
  priceRange?: {
    min: number;
    max: number;
  };
  starRating?: number[];
  amenities?: string[];
  searchTerm?: string;
  bounds?: { north: number; south: number; east: number; west: number };
}

interface HotelMapProps {
  hotels: Hotel[];
  onMarkerClick?: (hotelId: string) => void;
  hoveredHotelId?: string | null;
  onMarkerHover?: (hotelId: string | null) => void;
  autoFitBounds?: boolean;
  boundsPadding?: number;
  filters?: HotelFilters;
  onBoundsChanged?: (b: { north: number; south: number; east: number; west: number }) => void;
  /**
   * VOY-490: id → 1-based list index, used to label the new numbered
   * teardrop pins (`.map-pin`). When a hotel isn't in this map (e.g.
   * filtered out of the list but still rendered on the map by bounds),
   * the pin renders without a badge.
   */
  hotelOrder?: Map<string, number>;
  /**
   * VOY-490: explicit popup-CTA callbacks. Separate from
   * `onMarkerClick` so the pin click can keep its low-impact behavior
   * (open the popup, scroll to card if visible) while the popup
   * buttons take their own actions (switching view modes if needed,
   * triggering the room picker).
   */
  onViewInList?: (hotelId: string) => void;
  onViewRooms?: (hotelId: string) => void;
}

// Filter hotels based on applied filters
function filterHotels(hotels: Hotel[], filters?: HotelFilters): Hotel[] {
  if (!filters) return hotels;

  return hotels.filter(hotel => {
    if (filters.priceRange && hotel.price?.amount) {
      const price = hotel.price.amount;
      if (price < filters.priceRange.min || price > filters.priceRange.max) return false;
    }
    if (filters.starRating && filters.starRating.length > 0 && hotel.starRating) {
      if (!filters.starRating.includes(hotel.starRating)) return false;
    }
    if (filters.amenities && filters.amenities.length > 0 && hotel.amenities) {
      if (!filters.amenities.every(a => hotel.amenities!.includes(a))) return false;
    }
    if (filters.searchTerm && filters.searchTerm.trim()) {
      const s = filters.searchTerm.toLowerCase();
      if (
        !hotel.name.toLowerCase().includes(s) &&
        !hotel.location.toLowerCase().includes(s) &&
        !hotel.coordinates?.address?.toLowerCase().includes(s)
      ) return false;
    }
    return true;
  });
}

const CLUSTER_SOURCE = 'hotel-clusters';
const CLUSTER_LAYER = 'hotel-cluster-circles';
const CLUSTER_COUNT_LAYER = 'hotel-cluster-counts';

// Build a hotel lookup map from an array
function buildHotelMap(hotels: Hotel[]): Map<string, Hotel> {
  const m = new window.Map<string, Hotel>();
  for (const h of hotels) m.set(h.id, h);
  return m;
}

// Inner component that has access to the map context
function MapContent({
  filteredHotels,
  onMarkerClick,
  hoveredHotelId,
  onMarkerHover,
  autoFitBounds,
  boundsPadding,
  onBoundsChanged,
  hotelOrder,
  onViewInList,
  onViewRooms,
}: {
  filteredHotels: Hotel[];
  onMarkerClick?: (hotelId: string) => void;
  hoveredHotelId?: string | null;
  onMarkerHover?: (hotelId: string | null) => void;
  autoFitBounds: boolean;
  boundsPadding: number;
  onBoundsChanged?: (b: { north: number; south: number; east: number; west: number }) => void;
  hotelOrder?: Map<string, number>;
  onViewInList?: (hotelId: string) => void;
  onViewRooms?: (hotelId: string) => void;
}) {
  const { map, isLoaded } = useMap();
  const hasFitOnceRef = useRef(false);
  const userInteractedRef = useRef(false);
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
  const [visibleHotelIds, setVisibleHotelIds] = useState<Set<string>>(new Set());

  const hotelMap = useMemo(() => buildHotelMap(filteredHotels), [filteredHotels]);

  // Build GeoJSON for clustering
  const geojson = useMemo<GeoJSON.FeatureCollection<GeoJSON.Point>>(() => ({
    type: 'FeatureCollection',
    features: filteredHotels
      .filter(h => h.coordinates?.latitude && h.coordinates?.longitude)
      .map(h => ({
        type: 'Feature' as const,
        properties: { id: h.id },
        geometry: {
          type: 'Point' as const,
          coordinates: [h.coordinates!.longitude, h.coordinates!.latitude],
        },
      })),
  }), [filteredHotels]);

  // Track user interaction to disable auto-fit
  useEffect(() => {
    if (!map) return;
    const onInteract = () => { userInteractedRef.current = true; };
    map.on('dragstart', onInteract);
    map.on('zoomstart', onInteract);
    return () => {
      map.off('dragstart', onInteract);
      map.off('zoomstart', onInteract);
    };
  }, [map]);

  // Emit bounds changes
  useEffect(() => {
    if (!map || !onBoundsChanged) return;
    const emit = () => {
      const bounds = map.getBounds();
      onBoundsChanged({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
    };
    emit();
    map.on('moveend', emit);
    return () => { map.off('moveend', emit); };
  }, [map, onBoundsChanged]);

  // Auto-fit bounds on initial data load
  useEffect(() => {
    if (!map || !isLoaded || !autoFitBounds || userInteractedRef.current || hasFitOnceRef.current) return;

    const valid = filteredHotels.filter(h => h.coordinates?.latitude && h.coordinates?.longitude);
    if (valid.length === 0) return;

    if (valid.length === 1) {
      const h = valid[0];
      map.jumpTo({ center: [h.coordinates!.longitude, h.coordinates!.latitude], zoom: 13 });
    } else {
      const lngs = valid.map(h => h.coordinates!.longitude);
      const lats = valid.map(h => h.coordinates!.latitude);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);

      const lngSpan = maxLng - minLng;
      const latSpan = maxLat - minLat;
      const isVeryClose = latSpan < 0.01 && lngSpan < 0.01;

      if (isVeryClose) {
        const cLat = (minLat + maxLat) / 2;
        const cLng = (minLng + maxLng) / 2;
        map.fitBounds(
          [[cLng - 0.02, cLat - 0.02], [cLng + 0.02, cLat + 0.02]],
          { padding: 30, maxZoom: 14 }
        );
      } else {
        map.fitBounds(
          [
            [minLng - lngSpan * boundsPadding, minLat - latSpan * boundsPadding],
            [maxLng + lngSpan * boundsPadding, maxLat + latSpan * boundsPadding],
          ],
          { padding: 20, maxZoom: 15 }
        );
      }
    }
    hasFitOnceRef.current = true;
  }, [map, isLoaded, filteredHotels, autoFitBounds, boundsPadding]);

  // Set up cluster source + layers, and track which points are unclustered (visible as individual markers)
  useEffect(() => {
    if (!map || !isLoaded) return;

    // Add clustered GeoJSON source
    if (!map.getSource(CLUSTER_SOURCE)) {
      map.addSource(CLUSTER_SOURCE, {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    }

    // Cluster circle layer
    if (!map.getLayer(CLUSTER_LAYER)) {
      map.addLayer({
        id: CLUSTER_LAYER,
        type: 'circle',
        source: CLUSTER_SOURCE,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step', ['get', 'point_count'],
            '#3B82F6', // blue ≤5
            6, '#F59E0B', // amber 6-10
            11, '#EF4444', // red >10
          ],
          'circle-radius': [
            'step', ['get', 'point_count'],
            15, 6, 18, 11, 22,
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
          'circle-opacity': 0.9,
        },
      });
    }

    // Cluster count text
    if (!map.getLayer(CLUSTER_COUNT_LAYER)) {
      map.addLayer({
        id: CLUSTER_COUNT_LAYER,
        type: 'symbol',
        source: CLUSTER_SOURCE,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 12,
        },
        paint: { 'text-color': '#fff' },
      });
    }

    // Click cluster to zoom in
    const onClusterClick = (e: MapLibreGL.MapMouseEvent) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER] });
      if (!features.length) return;
      const clusterId = features[0].properties?.cluster_id;
      const source = map.getSource(CLUSTER_SOURCE) as MapLibreGL.GeoJSONSource;
      source.getClusterExpansionZoom(clusterId).then(zoom => {
        const geom = features[0].geometry;
        if (geom.type === 'Point') {
          map.easeTo({ center: geom.coordinates as [number, number], zoom });
        }
      });
    };

    // Cursor pointer on cluster hover
    const onClusterEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const onClusterLeave = () => { map.getCanvas().style.cursor = ''; };

    map.on('click', CLUSTER_LAYER, onClusterClick);
    map.on('mouseenter', CLUSTER_LAYER, onClusterEnter);
    map.on('mouseleave', CLUSTER_LAYER, onClusterLeave);

    // Compute visible unclustered hotel IDs on every render/move
    const updateVisible = () => {
      const source = map.getSource(CLUSTER_SOURCE) as MapLibreGL.GeoJSONSource | undefined;
      if (!source) return;
      // Query rendered unclustered points
      const features = map.querySourceFeatures(CLUSTER_SOURCE, {
        filter: ['!', ['has', 'point_count']],
      });
      const ids = new Set<string>();
      for (const f of features) {
        const id = f.properties?.id;
        if (id) ids.add(String(id));
      }
      setVisibleHotelIds(ids);
    };

    map.on('moveend', updateVisible);
    map.on('zoomend', updateVisible);
    // Initial compute after source loads
    map.once('idle', updateVisible);

    return () => {
      map.off('click', CLUSTER_LAYER, onClusterClick);
      map.off('mouseenter', CLUSTER_LAYER, onClusterEnter);
      map.off('mouseleave', CLUSTER_LAYER, onClusterLeave);
      map.off('moveend', updateVisible);
      map.off('zoomend', updateVisible);
      try {
        if (map.getLayer(CLUSTER_COUNT_LAYER)) map.removeLayer(CLUSTER_COUNT_LAYER);
        if (map.getLayer(CLUSTER_LAYER)) map.removeLayer(CLUSTER_LAYER);
        if (map.getSource(CLUSTER_SOURCE)) map.removeSource(CLUSTER_SOURCE);
      } catch { /* ignore */ }
    };
  }, [map, isLoaded, geojson]);

  // Update cluster source data when geojson changes
  useEffect(() => {
    if (!map || !isLoaded) return;
    const source = map.getSource(CLUSTER_SOURCE) as MapLibreGL.GeoJSONSource | undefined;
    if (source) source.setData(geojson);
  }, [map, isLoaded, geojson]);

  const handleMarkerClick = useCallback((hotelId: string) => {
    setSelectedHotelId(prev => prev === hotelId ? null : hotelId);
    onMarkerClick?.(hotelId);
  }, [onMarkerClick]);

  return (
    <>
      <MapControls position="bottom-right" showZoom showLocate />
      {/* VOY-490: numbered teardrop pins per Search+ design (was: price
          pill markers). Pin badge = 1-based position in the list, from
          the `hotelOrder` lookup the parent derives from
          `processedHotels`. Price moves into the popup, available on
          click. Clusters (multi-hotel circles) are unchanged — design
          doesn't override that treatment. */}
      {Array.from(visibleHotelIds).map(id => {
        const hotel = hotelMap.get(id);
        if (!hotel || !hotel.coordinates?.latitude || !hotel.coordinates?.longitude) return null;

        const price = hotel.price?.amount && hotel.price.amount > 0
          ? `$${Math.round(hotel.price.amount)}`
          : 'Check';

        const isHovered = hoveredHotelId === hotel.id;
        const isSelected = selectedHotelId === hotel.id;
        const img = Array.isArray(hotel.images) && hotel.images.length > 0 ? hotel.images[0] : '';
        const stars = typeof hotel.starRating === 'number'
          ? '★'.repeat(Math.round(hotel.starRating))
          : '';
        const orderIndex = hotelOrder?.get(hotel.id);
        const badge = typeof orderIndex === 'number' ? String(orderIndex) : '';

        return (
          <MapMarker
            key={hotel.id}
            longitude={hotel.coordinates.longitude}
            latitude={hotel.coordinates.latitude}
            onClick={() => handleMarkerClick(hotel.id)}
            onMouseEnter={() => onMarkerHover?.(hotel.id)}
            onMouseLeave={() => onMarkerHover?.(null)}
          >
            <MarkerContent>
              <div
                className={cn(
                  'map-pin',
                  // VOY-490: separate modifier classes so both CSS
                  // rules are actually reachable. Currently they have
                  // identical visuals (the popup itself is the
                  // strong "selected" signal), but the split lets us
                  // diverge later (e.g. a thicker ring on selected)
                  // without re-plumbing.
                  isHovered && 'map-pin--hovered',
                  isSelected && 'map-pin--selected'
                )}
                aria-label={`Hotel ${badge || hotel.name} on map`}
              >
                <span className="map-pin__inner">{badge}</span>
              </div>
            </MarkerContent>

            {isSelected && (
              <MarkerPopup closeButton>
                <div className="w-[280px]">
                  {img && (
                    <img
                      src={img}
                      alt={hotel.name}
                      className="w-full h-[140px] object-cover rounded-lg -mt-1 mb-2"
                    />
                  )}
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
                    {hotel.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                    {stars && <span className="text-amber-500 mr-1">{stars}</span>}
                    {hotel.coordinates?.address || ''}
                  </div>
                  <div className="font-bold text-base mb-2">{price}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // VOY-490: explicit popup-CTA callback (not
                        // the pin-click onMarkerClick). The parent's
                        // handleViewInList handles the view-switch
                        // from Map-only → Both if needed.
                        onViewInList?.(hotel.id);
                      }}
                      className="px-2.5 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
                    >
                      View in list
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // VOY-490: direct callback replaces the prior
                        // window.dispatchEvent('hotel-view-rooms')
                        // hack. The parent's handleViewRooms switches
                        // to Both view if needed, then clicks the
                        // per-card view-rooms button.
                        onViewRooms?.(hotel.id);
                      }}
                      className="px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium hover:bg-gray-800 transition-colors"
                    >
                      View rooms
                    </button>
                  </div>
                </div>
              </MarkerPopup>
            )}
          </MapMarker>
        );
      })}
    </>
  );
}

export function HotelMap({
  hotels,
  onMarkerClick,
  hoveredHotelId,
  onMarkerHover,
  autoFitBounds = true,
  boundsPadding = 0.1,
  filters,
  onBoundsChanged,
  hotelOrder,
  onViewInList,
  onViewRooms,
}: HotelMapProps) {
  const [isMapReady, setIsMapReady] = useState(false);

  // Filter hotels with valid coordinates
  const hotelsWithCoords = useMemo(
    () => hotels.filter(h => h.coordinates?.latitude && h.coordinates?.longitude),
    [hotels]
  );

  // Apply filters
  const filteredHotels = useMemo(() => {
    let result = filterHotels(hotelsWithCoords, filters);
    if (filters?.bounds) {
      const { north, south, east, west } = filters.bounds;
      result = result.filter(h => {
        const c = h.coordinates!;
        return c.latitude <= north && c.latitude >= south && c.longitude <= east && c.longitude >= west;
      });
    }
    return result;
  }, [hotelsWithCoords, filters]);

  const mapHasMarkers = hotelsWithCoords.length > 0;
  const mapHasFilteredMarkers = filteredHotels.length > 0;

  // Compute center
  const center = useMemo<[number, number]>(() => {
    if (filteredHotels.length > 0) {
      const avgLng = filteredHotels.reduce((s, h) => s + h.coordinates!.longitude, 0) / filteredHotels.length;
      const avgLat = filteredHotels.reduce((s, h) => s + h.coordinates!.latitude, 0) / filteredHotels.length;
      return [avgLng, avgLat];
    }
    return [0, 20];
  }, [filteredHotels]);

  const handleViewportChange = useCallback((_vp: MapViewport) => {
    if (!isMapReady) setIsMapReady(true);
  }, [isMapReady]);

  return (
    <div className="h-full w-full relative">
      {/* Loading overlay */}
      {!isMapReady && (
        <div className="absolute inset-0 bg-white/[0.08] dark:bg-zinc-800/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-xl">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="text-sm text-gray-700 dark:text-gray-300">Loading map...</p>
          </div>
        </div>
      )}

      {/* Filter indicator — only when filters actually reduce results */}
      {filters && filteredHotels.length < hotelsWithCoords.length && (
        <div className="absolute top-2 left-2 right-2 sm:right-auto z-[1000] bg-white/95 dark:bg-zinc-800/95 backdrop-blur-md rounded-lg px-3 py-2 shadow-lg border border-gray-200 dark:border-zinc-700/50">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                Showing {filteredHotels.length} of {hotelsWithCoords.length} hotels
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5" />
                Filters applied
              </div>
            </div>
            {filteredHotels.length > 0 && (
              <div className="ml-2 flex-shrink-0">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              </div>
            )}
          </div>
        </div>
      )}

      <Map
        center={center}
        zoom={12}
        className="h-full w-full rounded-xl"
        onViewportChange={handleViewportChange}
      >
        <MapContent
          filteredHotels={filteredHotels}
          onMarkerClick={onMarkerClick}
          hoveredHotelId={hoveredHotelId}
          onMarkerHover={onMarkerHover}
          autoFitBounds={autoFitBounds}
          boundsPadding={boundsPadding}
          onBoundsChanged={onBoundsChanged}
          hotelOrder={hotelOrder}
          onViewInList={onViewInList}
          onViewRooms={onViewRooms}
        />
      </Map>

      {/* No hotels state */}
      {!mapHasMarkers && hotels.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-gray-600 dark:text-gray-400 bg-white/90 dark:bg-zinc-800/90 backdrop-blur-md rounded-lg p-4 border border-gray-200 dark:border-zinc-700/50 shadow-lg">
            <div className="flex items-center justify-center mb-2">
              <MapIcon className="h-6 w-6 text-gray-400 dark:text-gray-500 mr-2" />
              <span className="font-medium text-gray-700 dark:text-gray-200">No hotels found</span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or filters</p>
          </div>
        </div>
      )}

      {/* Loading coordinates */}
      {!mapHasMarkers && hotels.length > 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-gray-600 dark:text-gray-400 bg-white/90 dark:bg-zinc-800/90 backdrop-blur-md rounded-lg p-4 border border-gray-200 dark:border-zinc-700/50 shadow-lg">
            <div className="flex items-center justify-center mb-2">
              <MapIcon className="h-6 w-6 text-gray-400 dark:text-gray-500 mr-2" />
              <span className="font-medium text-gray-700 dark:text-gray-200">Loading coordinates...</span>
            </div>
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {hotels.length} hotel{hotels.length !== 1 ? 's' : ''} found, getting locations...
            </p>
          </div>
        </div>
      )}

      {/* No filtered results */}
      {mapHasMarkers && !mapHasFilteredMarkers && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-white/95 dark:bg-zinc-800/95 backdrop-blur-md border border-gray-200 dark:border-zinc-700/50 rounded-lg px-4 py-2 shadow-lg">
          <div className="flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span className="text-gray-700 dark:text-gray-200">No hotels match current filters</span>
          </div>
        </div>
      )}
    </div>
  );
}
